if (!customElements.get('lusso-hero')) {
  customElements.define(
    'lusso-hero',
    class LussoHero extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-hero-track]');
        this.slides = [...this.querySelectorAll('[data-hero-slide]')];
        this.pagers = [...this.querySelectorAll('[data-hero-go]')];
        this.previousButton = this.querySelector('[data-hero-previous]');
        this.nextButton = this.querySelector('[data-hero-next]');
        this.currentLabel = this.querySelector('[data-hero-current]');
        this.status = this.querySelector('[data-hero-status]');
        this.desktopMedia = window.matchMedia('(min-width: 750px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
        this.interval = Number(this.dataset.interval) || 7000;
        this.autoplay = this.dataset.autoplay === 'true';
        this.userPaused = false;
        this.temporaryPause = false;
        this.scrollTicking = false;

        this.handlePrevious = () => this.show(this.index - 1, true);
        this.handleNext = () => this.show(this.index + 1, true);
        this.handlePager = (event) => this.show(Number(event.currentTarget.dataset.heroGo), true);
        this.handlePointerEnter = () => {
          if (this.desktopMedia.matches) this.setTemporaryPause(true);
        };
        this.handlePointerLeave = () => {
          if (this.desktopMedia.matches) this.setTemporaryPause(false);
        };
        this.handleFocusIn = () => this.setTemporaryPause(true);
        this.handleFocusOut = (event) => {
          if (!this.contains(event.relatedTarget)) this.setTemporaryPause(false);
        };
        this.handleVisibility = () => this.syncState();
        this.handleMediaChange = () => this.configureLayout();
        this.handleTrackScroll = () => this.onTrackScroll();
        this.handleKeydown = (event) => this.onKeydown(event);
        this.handleBlockSelect = (event) => this.onBlockSelect(event);

        this.previousButton?.addEventListener('click', this.handlePrevious);
        this.nextButton?.addEventListener('click', this.handleNext);
        this.pagers.forEach((pager) => pager.addEventListener('click', this.handlePager));
        this.addEventListener('pointerenter', this.handlePointerEnter);
        this.addEventListener('pointerleave', this.handlePointerLeave);
        this.addEventListener('focusin', this.handleFocusIn);
        this.addEventListener('focusout', this.handleFocusOut);
        this.addEventListener('keydown', this.handleKeydown);
        this.track?.addEventListener('scroll', this.handleTrackScroll, { passive: true });
        document.addEventListener('visibilitychange', this.handleVisibility);
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
        this.desktopMedia.addEventListener?.('change', this.handleMediaChange);
        this.reducedMotion.addEventListener?.('change', this.handleMediaChange);

        this.configureLayout();
      }

      disconnectedCallback() {
        this.stopTimer();
        this.pauseVideos();
        this.previousButton?.removeEventListener('click', this.handlePrevious);
        this.nextButton?.removeEventListener('click', this.handleNext);
        this.pagers.forEach((pager) => pager.removeEventListener('click', this.handlePager));
        this.removeEventListener('pointerenter', this.handlePointerEnter);
        this.removeEventListener('pointerleave', this.handlePointerLeave);
        this.removeEventListener('focusin', this.handleFocusIn);
        this.removeEventListener('focusout', this.handleFocusOut);
        this.removeEventListener('keydown', this.handleKeydown);
        this.track?.removeEventListener('scroll', this.handleTrackScroll);
        document.removeEventListener('visibilitychange', this.handleVisibility);
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
        this.desktopMedia?.removeEventListener?.('change', this.handleMediaChange);
        this.reducedMotion?.removeEventListener?.('change', this.handleMediaChange);
      }

      configureLayout() {
        if (this.desktopMedia.matches) {
          this.track?.scrollTo({ left: 0, behavior: 'instant' });
        }
        this.applyActiveState(false);
        this.syncState();
      }

      show(nextIndex, announce = false) {
        if (this.slides.length < 1) return;
        this.index = (nextIndex + this.slides.length) % this.slides.length;

        if (!this.desktopMedia.matches) {
          const target = this.slides[this.index];
          this.track?.scrollTo({
            left: target.offsetLeft,
            behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
          });
        }

        this.applyActiveState(announce);
        this.syncState();
      }

      applyActiveState(announce = false) {
        const desktop = this.desktopMedia.matches;
        this.classList.toggle('has-dark-active', this.slides[this.index]?.classList.contains('ls-hero__slide--dark'));
        this.classList.toggle('has-right-active', this.slides[this.index]?.querySelector('.ls-hero__content--x-right'));
        this.slides.forEach((slide, slideIndex) => {
          const active = slideIndex === this.index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', desktop && !active ? 'true' : 'false');
          slide.toggleAttribute('inert', desktop && !active);
        });

        this.pagers.forEach((pager, pagerIndex) => {
          const active = pagerIndex === this.index;
          pager.classList.toggle('is-active', active);
          pager.setAttribute('aria-current', active ? 'true' : 'false');
          if (active) {
            const progress = pager.querySelector('span');
            if (progress) {
              progress.style.animation = 'none';
              progress.offsetHeight;
              progress.style.animation = '';
            }
          }
        });

        if (this.currentLabel) this.currentLabel.textContent = String(this.index + 1).padStart(2, '0');
        if (announce && this.status) this.status.textContent = `Campaign ${this.index + 1} of ${this.slides.length}`;
      }

      onTrackScroll() {
        if (this.desktopMedia.matches || this.scrollTicking) return;
        this.scrollTicking = true;
        window.requestAnimationFrame(() => {
          const center = this.track.scrollLeft + this.track.clientWidth / 2;
          let closestIndex = 0;
          let closestDistance = Number.POSITIVE_INFINITY;
          this.slides.forEach((slide, index) => {
            const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
            const distance = Math.abs(center - slideCenter);
            if (distance < closestDistance) {
              closestDistance = distance;
              closestIndex = index;
            }
          });
          if (closestIndex !== this.index) {
            this.index = closestIndex;
            this.applyActiveState(true);
            this.syncState();
          }
          this.scrollTicking = false;
        });
      }

      onKeydown(event) {
        if (event.target.matches('a, button, input, textarea, select')) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.show(this.index - 1, true);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.show(this.index + 1, true);
        }
      }

      onBlockSelect(event) {
        const slideIndex = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail?.blockId);
        if (slideIndex < 0) return;
        this.setUserPaused(true);
        this.show(slideIndex, false);
      }

      setUserPaused(paused) {
        this.userPaused = paused;
        this.toggleButton?.setAttribute('aria-label', paused ? 'Play campaign' : 'Pause campaign');
        if (this.pauseIcon) this.pauseIcon.hidden = paused;
        if (this.playIcon) this.playIcon.hidden = !paused;
        this.syncState();
      }

      setTemporaryPause(paused) {
        this.temporaryPause = paused;
        this.syncState();
      }

      syncState() {
        this.stopTimer();
        const paused =
          this.userPaused ||
          this.temporaryPause ||
          this.reducedMotion.matches ||
          document.hidden;
        this.classList.toggle('is-paused', paused);
        this.syncVideos(paused);

        if (!this.autoplay || paused || this.slides.length < 2) return;
        this.timer = window.setTimeout(() => this.show(this.index + 1), this.interval);
      }

      syncVideos(paused) {
        this.slides.forEach((slide, index) => {
          const video = slide.querySelector('video');
          if (!video) return;
          if (index === this.index && this.desktopMedia.matches && !paused) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }

      pauseVideos() {
        this.querySelectorAll('video').forEach((video) => video.pause());
      }

      stopTimer() {
        if (!this.timer) return;
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    },
  );
}
